import { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';

// Helper function to convert string URL to Blob
const urlToBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url);
    return await response.blob();
};

// Helper function to create object URL
const createObjectURL = (blob: Blob): string => {
    return URL.createObjectURL(blob);
};

// Helper function to get an image element from a blob
const createImageFromBlob = async (blob: Blob): Promise<HTMLImageElement> => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = createObjectURL(blob);
    });
};

// Helper to get image dimensions
const getImageDimensions = async (image: File | Blob | string): Promise<{ width: number; height: number }> => {
    let blob: Blob;

    if (typeof image === 'string') {
        blob = await urlToBlob(image);
    } else {
        blob = image;
    }

    const img = await createImageFromBlob(blob);
    return { width: img.width, height: img.height };
};

interface UseImageProcessorOptions {
    image: File | Blob | string;
    onError?: (error: Error) => void;
}

export const useImageProcessor = ({ image, onError }: UseImageProcessorOptions) => {
    const [imageBlob, setImageBlob] = useState<Blob | null>(null);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

    useEffect(() => {
        const loadImage = async () => {
            try {
                setIsLoading(true);

                let blob: Blob;

                if (typeof image === 'string') {
                    blob = await urlToBlob(image);
                } else {
                    blob = image;
                }

                const imgDimensions = await getImageDimensions(blob);
                setDimensions(imgDimensions);
                setImageBlob(blob);
                setImageUrl(createObjectURL(blob));
            } catch (err) {
                console.error('Error loading image:', err);
                if (onError && err instanceof Error) {
                    onError(err);
                }
            } finally {
                setIsLoading(false);
            }
        };

        loadImage();

        // Cleanup
        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [image, onError]);

    // Resize image
    const resizeImage = async (width: number, height: number): Promise<Blob> => {
        if (!imageBlob) throw new Error('Image not loaded');

        const options = {
            maxWidthOrHeight: Math.max(width, height),
            useWebWorker: true,
        };

        const resizedBlob = await imageCompression(
            new File([imageBlob], 'image.jpg', { type: imageBlob.type }),
            options
        );

        // Use canvas for exact dimensions
        const img = await createImageFromBlob(resizedBlob);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        ctx.drawImage(img, 0, 0, width, height);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
            }, imageBlob.type);
        });
    };

    // Crop image
    const cropImage = async (cropX: number, cropY: number, width: number, height: number): Promise<Blob> => {
        if (!imageBlob) throw new Error('Image not loaded');

        const img = await createImageFromBlob(imageBlob);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        ctx.drawImage(img, cropX, cropY, width, height, 0, 0, width, height);

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
            }, imageBlob.type);
        });
    };

    // Compress image
    const compressImage = async (quality: number = 80, maxSizeMB?: number): Promise<Blob> => {
        if (!imageBlob) throw new Error('Image not loaded');

        const options = {
            maxSizeMB: maxSizeMB || 1,
            maxWidthOrHeight: 4096,
            useWebWorker: true,
            fileType: imageBlob.type,
            quality: quality / 100,
        };

        return await imageCompression(
            new File([imageBlob], 'image.jpg', { type: imageBlob.type }),
            options
        );
    };

    // Convert image format
    const convertFormat = async (format: string, quality: number = 80): Promise<Blob> => {
        if (!imageBlob) throw new Error('Image not loaded');

        const img = await createImageFromBlob(imageBlob);
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        ctx.drawImage(img, 0, 0);

        const mimeType = `image/${format}`;

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
            }, mimeType, quality / 100);
        });
    };

    // Apply effects
    const applyEffect = async (effect: string, intensity: number = 100): Promise<Blob> => {
        if (!imageBlob) throw new Error('Image not loaded');

        const img = await createImageFromBlob(imageBlob);
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) throw new Error('Could not get canvas context');

        // Draw the original image
        ctx.drawImage(img, 0, 0);

        // Apply the selected effect
        switch (effect) {
            case 'grayscale':
                const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const data = imageData.data;
                const intensityFactor = intensity / 100;

                for (let i = 0; i < data.length; i += 4) {
                    const gray = (data[i] + data[i + 1] + data[i + 2]) / 3;
                    const originalWeight = 1 - intensityFactor;

                    data[i] = data[i] * originalWeight + gray * intensityFactor;
                    data[i + 1] = data[i + 1] * originalWeight + gray * intensityFactor;
                    data[i + 2] = data[i + 2] * originalWeight + gray * intensityFactor;
                }

                ctx.putImageData(imageData, 0, 0);
                break;

            case 'sepia':
                const sepiaData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const sepiaPixels = sepiaData.data;
                const sepiaFactor = intensity / 100;

                for (let i = 0; i < sepiaPixels.length; i += 4) {
                    const r = sepiaPixels[i];
                    const g = sepiaPixels[i + 1];
                    const b = sepiaPixels[i + 2];

                    const newR = Math.min(255, (r * (1 - sepiaFactor)) + ((r * 0.393 + g * 0.769 + b * 0.189) * sepiaFactor));
                    const newG = Math.min(255, (g * (1 - sepiaFactor)) + ((r * 0.349 + g * 0.686 + b * 0.168) * sepiaFactor));
                    const newB = Math.min(255, (b * (1 - sepiaFactor)) + ((r * 0.272 + g * 0.534 + b * 0.131) * sepiaFactor));

                    sepiaPixels[i] = newR;
                    sepiaPixels[i + 1] = newG;
                    sepiaPixels[i + 2] = newB;
                }

                ctx.putImageData(sepiaData, 0, 0);
                break;

            case 'invert':
                const invertData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                const invertPixels = invertData.data;
                const invertFactor = intensity / 100;

                for (let i = 0; i < invertPixels.length; i += 4) {
                    invertPixels[i] = invertPixels[i] * (1 - invertFactor) + (255 - invertPixels[i]) * invertFactor;
                    invertPixels[i + 1] = invertPixels[i + 1] * (1 - invertFactor) + (255 - invertPixels[i + 1]) * invertFactor;
                    invertPixels[i + 2] = invertPixels[i + 2] * (1 - invertFactor) + (255 - invertPixels[i + 2]) * invertFactor;
                }

                ctx.putImageData(invertData, 0, 0);
                break;

            case 'blur':
                // Using CSS filter for blur as it's more efficient
                ctx.filter = `blur(${intensity / 10}px)`;
                ctx.drawImage(img, 0, 0);
                ctx.filter = 'none';
                break;

            case 'brightness':
                // Adjust brightness using CSS filter
                ctx.filter = `brightness(${intensity / 50})`;
                ctx.drawImage(img, 0, 0);
                ctx.filter = 'none';
                break;

            case 'contrast':
                // Adjust contrast using CSS filter
                ctx.filter = `contrast(${intensity / 50})`;
                ctx.drawImage(img, 0, 0);
                ctx.filter = 'none';
                break;

            case 'saturation':
                // Adjust saturation using CSS filter
                ctx.filter = `saturate(${intensity / 50})`;
                ctx.drawImage(img, 0, 0);
                ctx.filter = 'none';
                break;

            default:
                throw new Error(`Unsupported effect: ${effect}`);
        }

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) resolve(blob);
            }, imageBlob.type);
        });
    };

    return {
        imageBlob,
        imageUrl,
        isLoading,
        dimensions,
        resizeImage,
        cropImage,
        compressImage,
        convertFormat,
        applyEffect,
    };
}; 